/**
 * WithdrawStatus — статус заявки на вывод (Flow step="withdraw_status"). Показывает способ,
 * сумму/комиссию/к выплате, реквизиты назначения и статус (pending → completed либо
 * rejected/cancelled с возвратом на счёт). Пока pending — можно отменить (возврат холда).
 * Live-поллинг. Пропсы: withdrawal, live, onDone.
 */
import { useEffect, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { Card, FallbackButton, Row, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Note } from "@/components/Note";
import { withdrawalsApi, type ApiWithdrawal, type WithdrawalMethod } from "@/lib/api";
import { formatRub, formatUsdt, maskMiddle } from "@/lib/format";

type Props = {
  withdrawal: ApiWithdrawal;
  live: boolean;
  onDone: () => void;
};

const METHOD_LABEL: Record<WithdrawalMethod, string> = {
  card: "Зарубежная карта",
  swift: "SWIFT-перевод",
  crypto: "Крипто-кошелёк (TRC20)",
};

function isTerminal(s: string): boolean {
  return s === "completed" || s === "rejected" || s === "cancelled";
}

function destSummary(w: ApiWithdrawal): string {
  const d = w.destination || {};
  if (w.method === "card") return `Карта •••• ${(d.card_number || "").slice(-4)}`;
  if (w.method === "swift") return `${d.bank_name || "SWIFT"} · ${maskMiddle(d.iban || "", 4, 4)}`;
  return maskMiddle(d.wallet_address || "", 6, 6);
}

export function WithdrawStatus({ withdrawal, live, onDone }: Props) {
  const [current, setCurrent] = useState<ApiWithdrawal>(withdrawal);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!live || isTerminal(withdrawal.status)) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const fresh = await withdrawalsApi.get(withdrawal.id);
        if (!alive) return;
        setCurrent(fresh);
        if (isTerminal(fresh.status)) return;
      } catch {
        /* retry */
      }
      if (alive) timer = setTimeout(tick, 5000);
    };
    timer = setTimeout(tick, 5000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [withdrawal.id, withdrawal.status, live]);

  const done = current.status === "completed";
  const rejected = current.status === "rejected";
  const cancelled = current.status === "cancelled";
  const pending = current.status === "pending";

  const doCancel = async () => {
    setCancelling(true);
    try {
      setCurrent(await withdrawalsApi.cancel(current.id));
      setConfirming(false);
    } catch {
      /* оставляем как есть */
    } finally {
      setCancelling(false);
    }
  };

  useBackButton(onDone);
  useMainButton({ text: done ? "Готово" : "В кабинет", onClick: onDone, active: true });

  const title = cancelled
    ? "Вывод отменён"
    : rejected
    ? "Вывод отклонён"
    : done
    ? "Вывод выполнен"
    : current.method === "crypto"
    ? "Отправляем средства"
    : "Заявка на вывод создана";
  const heroIcon = cancelled ? "🚫" : rejected ? "❌" : done ? "✅" : "🏧";
  const heroSubtitle = done
    ? `${formatUsdt(current.net_usdt)} отправлены получателю`
    : rejected
    ? "Средства возвращены на счёт"
    : cancelled
    ? "Средства возвращены на счёт"
    : current.method === "crypto"
    ? "Перевод идёт автоматически — обычно занимает несколько минут"
    : "Оператор обработает заявку в ближайшее время";

  const createdAt = new Date(current.created_at).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Screen>
      <Hero icon={heroIcon} title={title} subtitle={heroSubtitle} />

      <div className="space-y-5">
        {pending && (
          <Note variant="warning">
            ⏳ Заявка в обработке. Средства уже зарезервированы со счёта — вы можете отменить заявку до
            её выполнения.
          </Note>
        )}
        {rejected && (
          <Note variant="danger">
            <div className="text-[15px] font-medium">Заявка отклонена</div>
            {current.cancel_reason && <div className="mt-2 text-[14px]">{current.cancel_reason}</div>}
            <div className="mt-2">Средства возвращены на счёт. По вопросам — напишите в поддержку.</div>
          </Note>
        )}

        <div>
          <div className="mb-2 text-[13px] font-medium text-white/55">Детали вывода</div>
          <Card>
            <Row label="Заявка" value={current.reference} mono />
            <Row label="Создана" value={createdAt} />
            <Row label="Способ" value={METHOD_LABEL[current.method]} />
            <Row label="Куда" value={destSummary(current)} mono />
            {current.source === "card" ? (
              <Row label="Списано с карты" value={formatUsdt(current.amount_usdt)} />
            ) : (
              current.amount_rub != null && <Row label="Списано со счёта" value={formatRub(current.amount_rub)} />
            )}
            {current.rate != null && current.rate > 0 && <Row label="Курс" value={`${current.rate.toFixed(2)} ₽/USD`} />}
            {current.fee_usdt > 0 && <Row label="Комиссия" value={formatUsdt(current.fee_usdt)} />}
            <Row label="К выплате" value={formatUsdt(current.net_usdt)} />
            {current.tx_hash && (
              <Row
                label="Транзакция"
                value={
                  <a
                    href={`https://tronscan.org/#/transaction/${current.tx_hash}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand"
                  >
                    {maskMiddle(current.tx_hash, 8, 6)} ↗
                  </a>
                }
                mono
              />
            )}
          </Card>
        </div>

        {pending &&
          (confirming ? (
            <Card>
              <div className="text-[14px]">Отменить заявку на вывод? Средства вернутся на счёт.</div>
              <div className="mt-3 flex gap-2">
                <button
                  disabled={cancelling}
                  onClick={doCancel}
                  className="flex-1 rounded-xl bg-red-500/15 px-4 py-3 text-[15px] text-red-300 disabled:opacity-50"
                >
                  {cancelling ? "Отмена…" : "Да, отменить"}
                </button>
                <button
                  disabled={cancelling}
                  onClick={() => setConfirming(false)}
                  className="flex-1 rounded-xl bg-white/[0.06] px-4 py-3 text-[15px]"
                >
                  Нет
                </button>
              </div>
            </Card>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="w-full py-2 text-center text-[14px] text-red-300 active:opacity-70"
            >
              Отменить заявку
            </button>
          ))}
      </div>

      <FallbackButton text={done ? "Готово" : "В кабинет"} onClick={onDone} />
    </Screen>
  );
}
